import { ImageWithMetadata, Slide } from '@features/powerpoint';
import { formatters } from '@utils/formattersV2';
import {
  Subdimension,
  SubdimensionsByName,
} from '@features/reports/subdomains/cpg';
import { shapes } from './shapes';
import { apiSlugToDimensionName } from '@components/Dashboard/dashboardSlice/responseMappers/dimensionStats';
import { FormSchema } from '@features/executive-report/subdomains/cpg';
import { images } from './images';
import { visibleSlide } from '@features/executive-report/slides';
import { DimensionRecommendationsSchema } from '@features/executive-report/subdomains/cpg';
import { DEFAULT_SLIDE_MASTER } from '@features/executive-report/slides/slide-master';
import { CpgRetailer } from '@features/campaigns/subdomains/cpg';
import { CampaignOverviewSlideOptions } from '../campaign-overview/campaign-overview';
import { headerTabs, TabId } from '../header-tabs';
import {
  retailerMetrics,
  SalesDimensionsSlideBaseOptions,
} from '@features/executive-report/slides/sales-dimensions/content';
import { texts } from '@features/executive-report/slides/sales-dimensions/texts';
import { overallLineItemTexts } from './texts';
import { TOTAL_DIMENSIONS } from '@features/executive-report/dimensions/layout';

type TabIdOptions = {
  activeHeaderTab: TabId;
  visible: boolean;
  getPerformanceMetrics: Parameters<typeof texts>[3];
  subdimensions: {
    name: string;
    incrementalSalesLift: number | null;
    incrementalSales: number | null;
    impressions: number | null;
    image?: ImageWithMetadata | null;
  }[];
};

export type SalesSlideOptions = SalesDimensionsSlideBaseOptions & TabIdOptions;

const salesDimensionsSlide = (options: SalesSlideOptions): Slide[] => {
  const subdimensions = options.subdimensions.slice(0, TOTAL_DIMENSIONS);

  return visibleSlide({
    slide: {
      slideMaster: DEFAULT_SLIDE_MASTER,
      texts: texts(
        { ...options, subdimensions },
        headerTabs(options.activeHeaderTab),
        overallLineItemTexts,
        options.getPerformanceMetrics
      ),
      shapes: shapes(subdimensions),
      images: images(subdimensions),
    },
    visible: options.visible,
  });
};

const subdimensionToSalesDimensionOptions =
  (subdimensionImages: FormSchema['subdimensionImagesByNameAndValue']) =>
  (
    subdimension: Pick<
      Subdimension,
      | 'dimensionValue'
      | 'incrementalSalesLift'
      | 'projectedIncrementalSales'
      | 'impressions'
      | 'dimensionName'
    >
  ): SalesDimensionsSlideBaseOptions['subdimensions'][number] => {
    return {
      name: subdimension.dimensionValue,
      incrementalSalesLift: subdimension.incrementalSalesLift,
      incrementalSales: subdimension.projectedIncrementalSales,
      impressions: subdimension.impressions,
      image:
        subdimensionImages[subdimension.dimensionName]?.[
          subdimension.dimensionValue
        ] ?? null,
    };
  };

const retailersToRetailerOptions = (
  retailer: Pick<
    CpgRetailer,
    | 'name'
    | 'incrementalSalesLift'
    | 'projectedIncrementalSales'
    | 'totalProjectedSales'
  >
): SalesDimensionsSlideBaseOptions['subdimensions'][number] => {
  return {
    name: retailer.name,
    placeholder: retailer.name,
    incrementalSalesLift: retailer.incrementalSalesLift,
    incrementalSales: retailer.projectedIncrementalSales,
    totalSales: retailer.totalProjectedSales,
    impressions: null,
    image: null,
  };
};

type SalesDimensionsSlidesOptions = {
  salesMetrics: CampaignOverviewSlideOptions['report'];
  subdimensionsByName: {
    [key in keyof SubdimensionsByName]: Parameters<
      ReturnType<typeof subdimensionToSalesDimensionOptions>
    >[0][];
  };
  recommendations: DimensionRecommendationsSchema;
  includedSlides: FormSchema['includedSlides']['sales'];
  subdimensionImages: FormSchema['subdimensionImagesByNameAndValue'];
  topRetailers: Parameters<typeof retailersToRetailerOptions>[0][];
};

export const salesDimensionsSlides = (
  options: SalesDimensionsSlidesOptions
): Slide[] => {
  const overallMetricsSales: SalesDimensionsSlideBaseOptions['overallMetrics'] =
    [
      {
        label: 'Incremental Sales and Lift',
        formattedValue: formatters.currency(
          options.salesMetrics.projectedIncrementalSales,
          { decimals: 1 }
        ),
        lift: options.salesMetrics.incrementalSalesLift ?? 0,
        liftFormatter: formatters.lift,
      },
      {
        label: 'Brand Transaction Size',
        formattedValue: formatters.currency(
          options.salesMetrics.averagePricePerTransaction,
          // TODO: used a shared formatter with web UI so we don't repeat this
          {
            decimals: 2,
          }
        ),
      },
      {
        label: 'Basket Size',
        formattedValue: formatters.currency(
          options.salesMetrics.averageTotalBasketAmount,
          {
            decimals: 2,
          }
        ),
      },
      {
        label: 'Purchase Frequency',
        formattedValue: `${formatters.number(
          options.salesMetrics.averagePurchaseFrequency,
          {
            decimals: 1,
          }
        )}x`,
      },
    ];

  // TODO: Refactor this for better type safety
  const salesDimensionsSlideOptions: SalesSlideOptions[] = [
    ...(options.subdimensionsByName.aud
      ? ([
          {
            dimensionName: apiSlugToDimensionName.aud,
            activeHeaderTab: 'who',
            subdimensions: options.subdimensionsByName.aud.map(
              subdimensionToSalesDimensionOptions(options.subdimensionImages)
            ),
            recommendation: options.recommendations.who,
            overallMetrics: overallMetricsSales,
            titleText: '<b>The</b><lci> WHO: </lci><b>Audiences</b>',
            visible: options.includedSlides.who,
          } as SalesSlideOptions,
        ] as const)
      : []),
    ...(options.subdimensionsByName.creative
      ? ([
          {
            dimensionName: apiSlugToDimensionName.creative,
            activeHeaderTab: 'what',
            subdimensions: options.subdimensionsByName.creative.map(
              subdimensionToSalesDimensionOptions(options.subdimensionImages)
            ),
            recommendation: options.recommendations.what,
            overallMetrics: overallMetricsSales,
            titleText: '<b>The</b><lci> WHAT: </lci><b>Creatives</b>',
            visible: options.includedSlides.what,
          } as SalesSlideOptions,
        ] as const)
      : []),
    ...(options.subdimensionsByName.channel
      ? ([
          {
            dimensionName: apiSlugToDimensionName.channel,
            activeHeaderTab: 'where',
            subdimensions: options.subdimensionsByName.channel.map(
              subdimensionToSalesDimensionOptions(options.subdimensionImages)
            ),
            recommendation: options.recommendations.where,
            overallMetrics: overallMetricsSales,
            titleText: '<b>The</b><lci> WHERE: </lci><b>Channels</b>',
            visible: options.includedSlides.where,
          } as SalesSlideOptions,
        ] as const)
      : []),
  ];
  const retailerSlideOptions: SalesSlideOptions[] =
    options.topRetailers.length > 0
      ? ([
          {
            dimensionName: 'Retailer',
            activeHeaderTab: 'how',
            subdimensions: options.topRetailers.map(retailer =>
              retailersToRetailerOptions(retailer)
            ),
            recommendation: options.recommendations.where,
            overallMetrics: overallMetricsSales,
            titleText: '<b>The</b><lci> HOW: </lci><b>Retailers</b>',
            visible: options.includedSlides.how,
            getPerformanceMetrics: retailerMetrics,
          } as SalesSlideOptions,
        ] as const)
      : [];

  return [...salesDimensionsSlideOptions, ...retailerSlideOptions].flatMap(
    options => salesDimensionsSlide(options)
  );
};
